import { useEffect, useState } from "react";
import { ArrowRight, History, RefreshCw } from "lucide-react";
import styled from "styled-components";
import { GD } from "src/GD";
import { useLocale } from "src/hooks/useLocale";
import { msg } from "src/locales/translate";
import { formatDisplayDateTime, parseServerDate } from "src/utils/dateTime";
import { DateTimeTooltip } from "../conponents/DateTimeTooltip";
import { humanize } from "../providers/providersData";
import { maskSecrets, ValueTree } from "../providers/ValueTree";
import { readStatementEvents, statementEventPayload, type StatementEvent } from "./statementEventsData";

const Surface = styled.div`
	min-height: 0; overflow: auto; flex: 1; padding: 0 20px 20px; background: #f5f7fa;
	font: 13px/1.5 "Euclid Circular B", sans-serif; color: #344054;
	.toolbar { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 12px; }
	.toolbar h3 { margin: 0; color: #667085; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .04em; }
	.refresh { display: inline-flex; align-items: center; gap: 6px; padding: 6px 10px; border: 1px solid #d0d5dd; border-radius: 6px; background: #fff; color: #344054; font: inherit; cursor: pointer; }
	.refresh:hover:not(:disabled) { background: #f9fafb; }
	.refresh:disabled { opacity: .5; cursor: default; }
	.refresh:focus-visible, summary:focus-visible { outline: 2px solid #84adff; outline-offset: 2px; }
	.card { overflow: hidden; background: #fff; border: 1px solid #d0d5dd; border-radius: 10px; box-shadow: 0 1px 2px rgba(16,24,40,.05); }
	.message { display: flex; align-items: flex-start; gap: 12px; padding: 20px; }
	.message > svg { flex-shrink: 0; margin-top: 2px; color: #98a2b3; }
	.message strong { display: block; color: #182230; font-weight: 600; }
	.message p { margin: 4px 0 0; color: #667085; }
	.message.error p { color: #b42318; }
	.event { padding: 18px 20px; }
	.event + .event { border-top: 1px solid #eaecf0; }
	.meta { display: flex; align-items: baseline; flex-wrap: wrap; gap: 4px 12px; }
	.action { color: #182230; font-weight: 600; }
	.author { color: #667085; font-size: 12px; }
	time { margin-left: auto; color: #98a2b3; font-size: 12px; }
	.transition { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-top: 10px; }
	.status { padding: 2px 8px; border: 1px solid #d0d5dd; border-radius: 6px; font-size: 12px; }
	.status.next { color: #175cd3; background: #eff8ff; border-color: #b2ddff; }
	.status.next[data-status="completed"], .status.next[data-status="calculated"] { color: #067647; background: #ecfdf3; border-color: #abefc6; }
	.status.next[data-status="rejected"] { color: #b42318; background: #fef3f2; border-color: #fecdca; }
	.reason { margin: 10px 0 0; white-space: pre-wrap; overflow-wrap: anywhere; }
	details { margin-top: 10px; }
	summary { width: fit-content; color: #667085; cursor: pointer; }
	.payload { margin-top: 12px; }
	@media (max-width: 600px) { padding-right: 16px; padding-left: 16px; time { width: 100%; margin-left: 0; } .event { padding: 16px; } }
`;

const EventItem = ({ event }: { event: StatementEvent }) => {
	useLocale();
	const payload = statementEventPayload(event.data);
	const statusChange = payload && (payload.old_status !== undefined || payload.new_status !== undefined);
	const author = event.user_name || (event.user_id ? msg("templateUser_039247f9", { v0: event.user_id }) : msg("system"));
	return (
		<article className="event">
			<div className="meta">
				<strong className="action">{/status.*change|change.*status/.test(event.action) ? msg("statusChanged") : humanize(event.action)}</strong>
				<span className="author">{author}</span>
				<time dateTime={parseServerDate(event.created_at)?.toISOString()}><DateTimeTooltip value={event.created_at}>{formatDisplayDateTime(event.created_at, { separator: " " })}</DateTimeTooltip></time>
			</div>
			{statusChange && <div className="transition">
				<span className="status">{String(payload.old_status ?? "—")}</span>
				<ArrowRight size={14} aria-hidden="true" />
				<span className="status next" data-status={String(payload.new_status ?? "")}>{String(payload.new_status ?? "—")}</span>
			</div>}
			{typeof payload?.reason === "string" && payload.reason && <p className="reason">{payload.reason}</p>}
			{payload && <details><summary>{msg("details")}</summary><div className="payload"><ValueTree value={maskSecrets(payload) as Record<string, unknown>} /></div></details>}
		</article>
	);
};

type LoadState = { status: "loading" } | { status: "error"; message: string } | { status: "ready"; events: StatementEvent[] };

export const StatementEventsTab = ({ statementId }: { statementId: number }) => {
	useLocale();
	const [project] = GD.B_SELECTED_PROJECT.use();
	const [user] = GD.REQ_PROFILE.useValue(undefined, undefined, [project]);
	const projectId = project?.id, userId = user?.id;
	const [version, setVersion] = useState(0);
	const query = `${projectId}:${userId}:${statementId}:${version}`;
	const [result, setResult] = useState<{ query: string; state: LoadState }>();
	// Hide a previous context's records synchronously, before its effect cleans up.
	const state: LoadState = result?.query === query ? result.state : { status: "loading" };
	useEffect(() => {
		const controller = new AbortController();
		const publish = (state: LoadState) => setResult({ query, state });
		publish({ status: "loading" });
		const logout = GD.S_AUTH_LOG_OUT.subscribe(() => {
			controller.abort();
			publish({ status: "error", message: msg("yourSessionWasTerminatedPleaseSignInAgain") });
		});
		const load = async () => {
			if (!projectId || !userId) { publish({ status: "error", message: msg("statementEventsLoadFailed") }); return; }
			try {
				// Use the existing generic audit endpoint; no browser-side history is fabricated.
				const response = await GD.REQ_API.request({ method: "events/list", data: { target_type: "statement", target_id: statementId }, expectedProjectId: projectId, signal: controller.signal });
				if (controller.signal.aborted) return;
				if (response?.error) { publish({ status: "error", message: response.error.message || msg("statementEventsUnavailable") }); return; }
				const events = readStatementEvents(response?.data, statementId);
				publish(events ? { status: "ready", events } : { status: "error", message: msg("statementEventsUnsupportedResponse") });
			} catch {
				if (!controller.signal.aborted) publish({ status: "error", message: msg("statementEventsLoadFailed") });
			}
		};
		void load();
		return () => { controller.abort(); GD.S_AUTH_LOG_OUT.unsubscribe(logout); };
	}, [statementId, projectId, userId, query]);

	return <Surface aria-busy={state.status === "loading"}>
		<div className="toolbar"><h3>{msg("events")}</h3><button className="refresh" type="button" disabled={state.status === "loading"} onClick={() => setVersion(value => value + 1)}><RefreshCw size={14} aria-hidden="true" />{msg("refresh")}</button></div>
		<div className="card">
			{state.status === "loading" && <div className="message" role="status"><History size={20} aria-hidden="true" /><span>{msg("loading")}</span></div>}
			{state.status === "error" && <div className="message error" role="alert"><History size={20} aria-hidden="true" /><div><strong>{msg("statementEventsUnavailable")}</strong><p>{state.message}</p></div></div>}
			{state.status === "ready" && (state.events.length ? state.events.map(event => <EventItem key={event.id} event={event} />) : <div className="message"><History size={20} aria-hidden="true" /><div><strong>{msg("statementEventsUnavailable")}</strong><p>{msg("statementEventsNoRecords")}</p></div></div>)}
		</div>
	</Surface>;
};
